import { Link } from '@tanstack/react-router';
import {
  Users,
  XCircle,
  RefreshCw,
  DollarSign,
  Tag,
  Trash2,
  AlertTriangle,
  ShieldCheck,
  KeyRound,
} from 'lucide-react';
import { useState } from 'react';

import DeleteTeamModal from '@/components/DeleteTeamModal';
import RemoveUsageCreditsModal from '@/components/RemoveUsageCreditsModal';
import TeamCreditsModal from '@/components/TeamCreditsModal';
import TeamDiscountModal from '@/components/TeamDiscountModal';
import TeamUsageCreditsModal from '@/components/TeamUsageCreditsModal';
import { useToast } from '@/components/ToastProvider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  useCancelTeamSubscription,
  useResumeTeamSubscription,
  useEnableTeamBaa,
  useDisableTeamBaa,
  useTransferTeamOwnership,
  useEnableTeamSso,
  useDisableTeamSso,
} from '@/hooks/useTeams';
import {
  formatCents,
  formatUnixDate,
  isHealthySubscriptionStatus,
} from '@/lib/utils';
import type { TeamForUserDetail } from '@/types/users';

interface UserEditTeamSectionProps {
  team: TeamForUserDetail;
  userId: number;
  userEmail: string;
}

export function statusVariant(status: string | null): {
  className: string;
  label: string;
} {
  switch (status) {
    case 'active':
      return {
        className: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        label: 'active',
      };
    case 'trialing':
      return {
        className: 'bg-blue-100 text-blue-700 border-blue-200',
        label: 'trialing',
      };
    case 'past_due':
      return {
        className: 'bg-amber-100 text-amber-700 border-amber-200',
        label: 'past due',
      };
    case 'canceled':
      return {
        className: 'bg-zinc-200 text-zinc-700 border-zinc-300',
        label: 'canceled',
      };
    default:
      return {
        className: 'bg-zinc-200 text-zinc-700 border-zinc-300',
        label: status || 'no subscription',
      };
  }
}

export default function UserEditTeamSection({
  team,
  userId,
  userEmail,
}: UserEditTeamSectionProps) {
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showUsageCreditsModal, setShowUsageCreditsModal] = useState(false);
  const [showRemoveUsageCreditsModal, setShowRemoveUsageCreditsModal] =
    useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const cancelSub = useCancelTeamSubscription();
  const resumeSub = useResumeTeamSubscription();
  const enableBaa = useEnableTeamBaa();
  const disableBaa = useDisableTeamBaa();
  const transferOwnership = useTransferTeamOwnership();
  const enableSso = useEnableTeamSso();
  const disableSso = useDisableTeamSso();
  const toast = useToast();

  const status = statusVariant(team.subscription_status);
  const isCanceled = team.subscription_status === 'canceled';
  const hasSubscription = isHealthySubscriptionStatus(team.subscription_status);
  const activeMemberCount = team.members.filter(
    (m) => m.status === 'active'
  ).length;

  const handleCancel = async () => {
    const ok = window.confirm(
      `Cancel ${team.name} subscription?\n\n` +
        `This affects all ${activeMemberCount} team members, not just ${userEmail}.\n` +
        `Subscription remains active until the end of the current billing period.\n` +
        `After that, all members will be downgraded to the free plan.`
    );
    if (!ok) return;
    try {
      const res = await cancelSub.mutateAsync({ teamId: team.id, userId });
      toast.success(
        'Team subscription cancelled',
        `${res.team_name} (${res.affected_member_count} members affected) — ${res.message}`
      );
    } catch (e) {
      toast.error(
        'Failed to cancel team subscription',
        e instanceof Error ? e.message : 'Unknown error'
      );
    }
  };

  const handleResume = async () => {
    try {
      const res = await resumeSub.mutateAsync({ teamId: team.id, userId });
      toast.success('Team subscription resumed', res.message);
    } catch (e) {
      toast.error(
        'Failed to resume team subscription',
        e instanceof Error ? e.message : 'Unknown error'
      );
    }
  };

  const handleEnableBaa = async () => {
    const legalName = window.prompt(
      `Enable HIPAA BAA for ${team.name}?\n\n` +
        `This enforces privacy mode on all ${activeMemberCount} current members and anyone who joins later.\n\n` +
        `Enter the legal name of the person authorizing this BAA:`
    );
    if (!legalName || !legalName.trim()) return;
    try {
      await enableBaa.mutateAsync({
        teamId: team.id,
        userId,
        legalName: legalName.trim(),
      });
      toast.success(
        'Team BAA enabled',
        'HIPAA is now enforced for the whole team.'
      );
    } catch (e) {
      toast.error(
        'Failed to enable team BAA',
        e instanceof Error ? e.message : 'Unknown error'
      );
    }
  };

  const handleDisableBaa = async () => {
    const ok = window.confirm(
      `Disable HIPAA BAA for ${team.name}?\n\n` +
        `This removes team-enforced privacy mode from members who did not individually sign their own BAA. ` +
        `Members who self-signed keep HIPAA enforced.`
    );
    if (!ok) return;
    try {
      await disableBaa.mutateAsync({ teamId: team.id, userId });
      toast.success('Team BAA disabled', 'HIPAA team enforcement removed.');
    } catch (e) {
      toast.error(
        'Failed to disable team BAA',
        e instanceof Error ? e.message : 'Unknown error'
      );
    }
  };

  const handleTransferOwnership = async (
    membershipId: string,
    email: string
  ) => {
    const confirmed = window.confirm(
      `Make ${email} the owner of ${team.name}?\n\n` +
        `The current owner becomes an admin. Billing emails are unchanged.`
    );
    if (!confirmed) return;
    try {
      await transferOwnership.mutateAsync({
        teamId: team.id,
        userId,
        membershipId,
      });
      toast.success('Ownership transferred', `${email} is now the owner.`);
    } catch (e) {
      toast.error(
        'Failed to transfer ownership',
        e instanceof Error ? e.message : 'Unknown error'
      );
    }
  };

  const handleToggleBaa = (next: boolean) => {
    if (next) {
      void handleEnableBaa();
    } else {
      void handleDisableBaa();
    }
  };

  const handleEnableSso = async () => {
    const ok = window.confirm(
      `Enable SSO for ${team.name}?\n\n` +
        `Team admins can then connect their identity provider from team settings.`
    );
    if (!ok) return;
    try {
      await enableSso.mutateAsync({ teamId: team.id, userId });
      toast.success(
        'Team SSO enabled',
        'Team admins can now set up SSO in team settings.'
      );
    } catch (e) {
      toast.error(
        'Failed to enable team SSO',
        e instanceof Error ? e.message : 'Unknown error'
      );
    }
  };

  const handleDisableSso = async () => {
    const ok = window.confirm(
      `Disable SSO for ${team.name}?\n\n` +
        `Members on Require SSO can sign in any way again. ` +
        `The connection and domains are kept, so enabling SSO again restores them.`
    );
    if (!ok) return;
    try {
      await disableSso.mutateAsync({ teamId: team.id, userId });
      toast.success(
        'Team SSO disabled',
        'SSO sign-in turned off for the team.'
      );
    } catch (e) {
      toast.error(
        'Failed to disable team SSO',
        e instanceof Error ? e.message : 'Unknown error'
      );
    }
  };

  const handleToggleSso = (next: boolean) => {
    if (next) {
      void handleEnableSso();
    } else {
      void handleDisableSso();
    }
  };

  return (
    <>
      <Card className="border-l-4 border-l-primary">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Team: {team.name}
                <Link
                  to="/teams/$teamId"
                  params={{ teamId: team.id }}
                  className="text-sm font-normal text-primary hover:underline"
                >
                  Open team →
                </Link>
              </CardTitle>
              <CardDescription>
                <span className="capitalize">{team.membership_tier}</span> ·{' '}
                <span className="capitalize">{team.membership_role}</span>
                {team.billing_interval && (
                  <>
                    {' · '}
                    <span className="capitalize">{team.billing_interval}</span>
                  </>
                )}
              </CardDescription>
            </div>
            <div className="flex flex-col items-end gap-2">
              <Badge variant="outline" className={status.className}>
                {status.label}
              </Badge>
              {team.baa_enabled && (
                <Badge
                  variant="outline"
                  className="bg-emerald-100 text-emerald-700 border-emerald-200"
                >
                  HIPAA BAA
                </Badge>
              )}
              <span className="inline-flex items-center rounded-full bg-primary/10 text-primary px-2.5 py-0.5 text-xs font-medium">
                Affects all {activeMemberCount} team members
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Cancel-at-period-end banner */}
          {team.cancel_at_period_end &&
            team.current_period_end &&
            !isCanceled && (
              <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 px-3 py-2.5">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-amber-900 dark:text-amber-200">
                    Subscription will cancel on{' '}
                    {formatUnixDate(team.current_period_end)}. All{' '}
                    {activeMemberCount} members will be downgraded to free.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2"
                    onClick={handleResume}
                    disabled={resumeSub.isPending}
                  >
                    {resumeSub.isPending ? 'Resuming...' : "Don't cancel"}
                  </Button>
                </div>
              </div>
            )}

          {/* Past due banner */}
          {team.subscription_status === 'past_due' && (
            <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 px-3 py-2.5">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900 dark:text-amber-200">
                Last invoice failed. Customer must update payment.
              </p>
            </div>
          )}

          {/* Trialing banner */}
          {team.subscription_status === 'trialing' && team.trial_end && (
            <div className="flex items-start gap-2 rounded-md bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 px-3 py-2.5">
              <Tag className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
              <p className="text-sm text-blue-900 dark:text-blue-200">
                Trial ends {formatUnixDate(team.trial_end)}. Subscription will
                cancel if no payment method is added.
              </p>
            </div>
          )}

          {/* Stat strip */}
          <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
            <div className="border rounded-md p-3">
              <p className="text-xs text-muted-foreground">Seats</p>
              <p className="text-xl font-semibold mt-1">{team.seat_count}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {[
                  team.plus_seats > 0 && `${team.plus_seats} Plus`,
                  team.power_seats > 0 && `${team.power_seats} Power`,
                  team.pro_seats > 0 && `${team.pro_seats} Pro`,
                  team.pro2_seats > 0 && `${team.pro2_seats} Pro2`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
            <div className="border rounded-md p-3">
              <p className="text-xs text-muted-foreground">
                {team.cancel_at_period_end ? 'Cancels' : 'Renews'}
              </p>
              <p className="text-sm font-semibold mt-1">
                {formatUnixDate(team.current_period_end)}
              </p>
            </div>
            <div className="border rounded-md p-3">
              <p className="text-xs text-muted-foreground">
                Team usage credits
              </p>
              <p className="text-xl font-semibold mt-1">
                {team.team_wallet_purchased_balance.toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {formatCents(
                  (team.team_wallet_purchased_balance *
                    team.rate_cents_per_1000) /
                    1000
                )}{' '}
                · shared pool
              </p>
            </div>
            <div className="border rounded-md p-3">
              <p className="text-xs text-muted-foreground">
                Stripe invoice credit
              </p>
              <p className="text-sm font-semibold mt-1">
                {formatCents(team.account_credit_cents)}
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2 pt-2 border-t">
            <Button
              variant="destructive"
              size="sm"
              onClick={handleCancel}
              disabled={
                !hasSubscription ||
                team.cancel_at_period_end ||
                cancelSub.isPending
              }
            >
              <XCircle className="h-4 w-4 mr-1" />
              {cancelSub.isPending
                ? 'Cancelling...'
                : team.cancel_at_period_end
                  ? 'Cancel scheduled'
                  : 'Cancel subscription'}
            </Button>
            {team.cancel_at_period_end && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleResume}
                disabled={resumeSub.isPending}
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                {resumeSub.isPending ? 'Resuming...' : 'Resume subscription'}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCreditsModal(true)}
              disabled={!team.stripe_customer_id}
            >
              <DollarSign className="h-4 w-4 mr-1" />
              Add Stripe invoice credit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowUsageCreditsModal(true)}
            >
              <DollarSign className="h-4 w-4 mr-1" />
              Add usage credits
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowRemoveUsageCreditsModal(true)}
            >
              <DollarSign className="h-4 w-4 mr-1" />
              Remove usage credits
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDiscountModal(true)}
              disabled={!hasSubscription}
            >
              <Tag className="h-4 w-4 mr-1" />
              Apply team discount
            </Button>
            <div className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5">
              <ShieldCheck className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">HIPAA BAA</span>
              <Switch
                checked={team.baa_enabled}
                disabled={enableBaa.isPending || disableBaa.isPending}
                onCheckedChange={handleToggleBaa}
                aria-label="Toggle HIPAA BAA for team"
              />
            </div>
            <div className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5">
              <KeyRound className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">SSO</span>
              <Switch
                checked={team.sso_enabled}
                disabled={enableSso.isPending || disableSso.isPending}
                onCheckedChange={handleToggleSso}
                aria-label="Toggle SSO for team"
              />
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowDeleteModal(true)}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete team
            </Button>
          </div>

          {/* Member list */}
          <div>
            <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">
              Team members
            </p>
            <div className="border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground">
                      Email
                    </th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground">
                      Role
                    </th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground">
                      Tier
                    </th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {team.members.map((m) => (
                    <tr key={m.membership_id} className="border-t">
                      <td className="px-3 py-2">
                        {m.email}
                        {m.email === userEmail && (
                          <span className="ml-2 inline-flex items-center rounded-full bg-primary/10 text-primary px-1.5 py-0.5 text-[10px] font-medium">
                            this user
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 capitalize">
                        {m.role}
                        {m.status === 'active' && m.role !== 'owner' && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="ml-2 h-6 px-2 text-xs normal-case"
                            onClick={() =>
                              handleTransferOwnership(m.membership_id, m.email)
                            }
                            disabled={transferOwnership.isPending}
                          >
                            Make owner
                          </Button>
                        )}
                      </td>
                      <td className="px-3 py-2 capitalize">{m.tier}</td>
                      <td className="px-3 py-2">
                        <Badge
                          variant="outline"
                          className={
                            m.status === 'active'
                              ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                              : m.status === 'invited'
                                ? 'bg-blue-100 text-blue-700 border-blue-200'
                                : 'bg-zinc-200 text-zinc-700 border-zinc-300'
                          }
                        >
                          {m.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>

      <TeamCreditsModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
        teamId={team.id}
        teamName={team.name}
        userId={userId}
      />
      <TeamUsageCreditsModal
        isOpen={showUsageCreditsModal}
        onClose={() => setShowUsageCreditsModal(false)}
        teamId={team.id}
        userId={userId}
        rateCentsPer1000={team.rate_cents_per_1000}
      />
      <RemoveUsageCreditsModal
        isOpen={showRemoveUsageCreditsModal}
        onClose={() => setShowRemoveUsageCreditsModal(false)}
        wallet={{ kind: 'team', teamId: team.id, userId }}
        purchasedBalance={team.team_wallet_purchased_balance}
        rateCentsPer1000={team.rate_cents_per_1000}
      />
      <TeamDiscountModal
        isOpen={showDiscountModal}
        onClose={() => setShowDiscountModal(false)}
        teamId={team.id}
        teamName={team.name}
        userId={userId}
      />
      <DeleteTeamModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        team={team}
        userId={userId}
      />
    </>
  );
}
